import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { PremiumCard } from '../premium';
import { normalizeActivityType, WORKOUT_ACTIVITY_TYPES } from '../../lib/workoutActivity';
import { formatVolume, workoutSetCount } from '../../lib/workoutStats';
import type { Workout } from '../../types';

function StatChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-2.5 py-1 text-[10px] font-semibold tabular-nums text-[var(--text-secondary)]">
      {children}
    </span>
  );
}

export function LastWorkoutSummaryCard({ workout, imperial }: { workout: Workout; imperial: boolean }) {
  const nav = useNavigate();
  const sets = workoutSetCount(workout);
  const volume = formatVolume(workout, imperial);
  const date = new Date(workout.startedAt);
  const dateLabel = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const duration =
    workout.durationMinutes && workout.durationMinutes > 0 ? `${workout.durationMinutes} min` : null;
  const kcal =
    workout.estimatedCaloriesBurned != null && workout.estimatedCaloriesBurned > 0
      ? `~${workout.estimatedCaloriesBurned} kcal`
      : null;
  const actLabel =
    WORKOUT_ACTIVITY_TYPES.find((a) => a.id === normalizeActivityType(workout.activityType))?.label ?? null;

  const chips: string[] = [];
  if (duration) chips.push(duration);
  if (sets > 0) chips.push(`${sets} sets`);
  if (volume) chips.push(volume);
  if (kcal) chips.push(kcal);

  return (
    <PremiumCard
      variant="default"
      className="!border-white/8 !bg-white/[0.03] !p-0 !shadow-none"
      onClick={() => nav(`/workouts/history/${workout.id}`)}
    >
      <div className="flex items-start gap-3 p-3.5">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--text-muted)]">Last workout</p>
          <h3 className="mt-1 truncate font-display text-[15px] font-bold text-[var(--text-primary)]">{workout.name}</h3>
          {actLabel ? (
            <p className="mt-0.5 truncate text-[11px] text-sky-300/80">{actLabel}</p>
          ) : null}
          <p className="mt-1 text-xs text-[var(--text-muted)]">{dateLabel}</p>
          {chips.length > 0 ? (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {chips.map((chip) => (
                <StatChip key={chip}>{chip}</StatChip>
              ))}
            </div>
          ) : null}
        </div>
        <span className="mt-2 shrink-0 text-sm text-[var(--text-muted)]">›</span>
      </div>
    </PremiumCard>
  );
}
